import { useContext, useEffect, useState } from "react";
import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";

import { api } from "../../api.js";
import { ManagedLocationsContext } from "../../Contexts.js";
import {
  Button,
  LoadingSpinner,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";

interface LocalAuthorityMappingsPickerProps {
  teamId: string;
}

export function LocalAuthorityMappingsPicker({
  teamId,
}: LocalAuthorityMappingsPickerProps) {
  const { managedLocations } = useContext(ManagedLocationsContext);
  const localAuthorities = managedLocations.filter(
    (loc) => loc.type === "localAuthority" && loc.status === "active",
  );

  const [selectedLaIds, setSelectedLaIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load existing mappings
  useEffect(() => {
    const loadMappings = async () => {
      try {
        const response = await api.teams.getLocalAuthorityMappings(teamId);
        setSelectedLaIds(response.data.localAuthorityIds);
        setIsLoaded(true);
      } catch (error) {
        console.error("Failed to load LA mappings:", error);
        setIsLoaded(true);
      }
    };
    void loadMappings();
  }, [teamId]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.teams.setLocalAuthorityMappings(teamId, selectedLaIds);
      renderSuccessToast({
        message: "Local authority assignments updated",
      });
    } catch (error) {
      console.error("Failed to save LA mappings:", error);
      renderErrorToast({
        message: "Failed to update local authority assignments",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isLoaded) {
    return <LoadingSpinner label="Loading local authority assignments..." />;
  }

  const selectedLas = localAuthorities.filter((la) =>
    selectedLaIds.includes(la.id),
  );

  return (
    <Box sx={{ maxWidth: "600px", mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Local Authority Assignments
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 2,
        }}
      >
        Select the local authorities whose payment requests this team can
        review.
      </Typography>
      <Autocomplete
        multiple
        options={localAuthorities}
        getOptionLabel={(option) => option.name}
        value={selectedLas}
        onChange={(_event, newValue) => {
          setSelectedLaIds(newValue.map((la) => la.id));
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip key={key} label={option.name} size="small" {...tagProps} />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Local Authorities"
            placeholder="Search local authorities..."
            size="small"
          />
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        sx={{ mb: 2 }}
      />
      <Button
        disabled={isSaving}
        label={isSaving ? "Saving..." : "Update Assignments"}
        onClick={() => {
          void handleSave();
        }}
      />
    </Box>
  );
}
